<template>
  <StepLayout
    :back="back"
    :next="next"
    :isEditRoute="true"
    helpscoutArticle="66589172077fa94da464eb9b"
    buttonDataEc="envelopes_reminders_page"
  >
    <template v-slot:title>
      {{ $t('ui.envelopes.create.steps.reminders.title') }}
    </template>
    <p class="text-black">
      {{ $t('ui.envelopes.create.steps.reminders.description') }}
    </p>

    <div
      v-if="!isPendingEnvelope"
      class="flex flex-col sm:grid-flow-col sm:auto-cols-max sm:grid sm:grid-rows-auto4 sm:gap-x-xxl gap-xs mb-base"
    >
      <div
        v-for="option in reminderOptions"
        class="flex items-center first:col-span-2 mb-sm first:mb-twenty"
        :key="option.value"
      >
        <VCheckbox
          :id="`reminder.${option.value}`"
          name="reminder"
          :checked="reminder === option.value"
          @input="updateReminder(option.value)"
          type="radio"
          data-ec="envelopes_reminders_page"
          :data-et="`radio_option_${option.value}`"
        />
        <label
          :for="`reminder.${option.value}`"
          class="cursor-pointer pl-base"
        >
          {{ option.option }}
        </label>
      </div>
    </div>

    <HeightTransition v-if="!isPendingEnvelope">
      <div
        v-if="isCustomReminder"
        class="flex flex-col max-w-screen-sm"
      >
        <FloatingLabelInput
          class="mt-base"
          type="number"
          :min="0"
          :step="1"
          :value="customReminder"
          @input="updateCustomReminder"
          :fieldIsRequired="false"
          :placeholder="
            $t('ui.envelopes.create.steps.reminders.customInterval')
          "
          :aria-label="$t('ui.envelopes.create.steps.reminders.customInterval')"
          data-test="reminders-custom_interval"
          data-hj-suppress
          data-ec="envelopes_reminders_page"
          data-et="custom_interval_input"
        />
        <ValidationError
          :hasError="hasErrors('auto_remind_hours')"
          :errorMessage="getError('auto_remind_hours')"
        />
      </div>
    </HeightTransition>
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router/composables';

import {
  useCreateUpdateEnvelope,
  useEnvelope,
  useEnvelopesStepsNavigation,
} from '@/composables/envelopes';
import {
  useErrors,
  useUpdateWithValidation,
  useValidation,
} from '@/composables/envelopes/validation';
import { watchFalse } from '@/composables/utils/watchFalse';
import { EnvelopeUpdate } from '@/store/modules/envelopes/models/Envelope';
import i18n from '@/locales';

import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import StepLayout from '@/components/layouts/StepLayout';
import ValidationError from '@/components/library/error/ValidationError/ValidationError.vue';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import HeightTransition from '@/transitions/HeightTransition.vue';

const route = useRoute();

const { envelope, isPendingEnvelope } = useEnvelope(route.params.fingerprint);
const { updateEnvelope } = useCreateUpdateEnvelope(route.params.fingerprint);

// initialise local state
/** @type {Array<{ value: number | 'custom'; option: string }>} */
const reminderOptions = [
  {
    value: 0,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.noReminders'),
  },
  {
    value: 1,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.everyday'),
  },
  {
    value: 2,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every2Days'),
  },
  {
    value: 3,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every3Days'),
  },
  {
    value: 5,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every5Days'),
  },
  {
    value: 7,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every7Days'),
  },
  {
    value: 'custom',
    option: i18n.t('ui.envelopes.create.steps.reminders.options.custom'),
  },
];
const reminderValues = reminderOptions.map(option => option.value);

const customReminder = ref();
const reminder = ref();
watchFalse(isPendingEnvelope, () => {
  const days = Math.floor((envelope.value?.auto_remind_hours || 0) / 24);

  customReminder.value = !reminderValues.includes(days) ? days : null;
  reminder.value = customReminder.value ? 'custom' : days;
});

const isCustomReminder = computed(() => reminder.value === 'custom');

const reminderUpdate = computed(() => {
  /** @type {number|undefined} */
  let hours = reminder.value * 24;
  if (reminder.value === 'custom') {
    hours = customReminder.value ? customReminder.value * 24 : undefined;
  }

  return {
    auto_remind_hours: hours,
  };
});

const ReminderUpdate = EnvelopeUpdate.pick({
  auto_remind_hours: true,
}).required();

const {
  isValid,
  data,
  errors,
  validate,
} = useValidation(
  reminderUpdate,
  ReminderUpdate,
);

const { hasErrors, getError } = useErrors(errors);

/** @param {number|'custom'} value */
function updateReminder(value) {
  customReminder.value = null;
  reminder.value = value;

  if (value !== 'custom') {
    validate();
  }
}

/** @param {string} value */
function updateCustomReminder(value) {
  customReminder.value = parseInt(value, 10);
  validate();
}

const update = useUpdateWithValidation(updateEnvelope, data, validate);

const { back, next } = useEnvelopesStepsNavigation(update, isValid);
</script>

